<template>
  <div class="dialog-step">
    <div class="dialog-step__content">
      <BurnLiquidityOutput class="output" />
      <BurnLiquidityInfo />
    </div>
    <div class="dialog-step__footer mt-3">
      <p-button
        class="button p-button-primary justify-content-center w-full"
        :class="{
          'rate-changed': buttonState.rateChanged,
        }"
        @click="onConfirm"
      >
        <span> {{ buttonState.text }} </span>
        <div class="progress" :style="animation"></div>
      </p-button>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { BurnLiquidityOutput } from '@/components/BurnLiquidity';
import BurnLiquidityInfo from './BurnLiquidityInfo.vue';
import { computed, ref, watch } from 'vue';
import { ANIMATION_PAUSE_TIME, LOADING_ANIMATION_TIME } from '@/constants';
import { useBurnLiquidityStore } from '@/store';
import { useI18n } from '@/composables/useI18n';

const { t } = useI18n();

const ratesHasChanged = ref(false);
const animationPause = ref(false);

const burnStore = useBurnLiquidityStore();

const xOutputCache = ref(burnStore.output?.x);
const yOutputCache = ref(burnStore.output?.y);

const emits = defineEmits(['close', 'confirm']);

const isSoloPool = ref(!!burnStore.pool?.depositToken);

const isDanger = computed(
  () =>
    burnStore.output &&
    ((xOutputCache.value &&
      isDangerSituation(burnStore.output.x, xOutputCache.value)) ||
      (yOutputCache.value &&
        isDangerSituation(burnStore.output.y, yOutputCache.value))),
);

const buttonState = computed(() => {
  if (ratesHasChanged.value) {
    return { rateChanged: true, text: t('errors.notePriceChanged') };
  }
  return { text: t('common.confirm') };
});

const animation = ref(
  buttonState.value.rateChanged
    ? {
        animation: 'progress ' + LOADING_ANIMATION_TIME + 'ms ease 0s',
      }
    : undefined,
);

/**
 * Returns true if output changes by 5% or more
 *
 * @returns boolean
 */

const isDangerSituation = (current: number, previous: number) => {
  const percentDifference = 100 - (current / previous) * 100;
  return percentDifference >= 5;
};

function onConfirm() {
  emits('confirm');
}

watch(
  () => [burnStore.output?.x, burnStore.output?.y],
  () => {
    if (isDanger.value && !animationPause.value) {
      ratesHasChanged.value = true;
      animationPause.value = true;
      setTimeout(() => {
        ratesHasChanged.value = false;
      }, LOADING_ANIMATION_TIME);
      setTimeout(() => {
        animationPause.value = false;
      }, LOADING_ANIMATION_TIME + ANIMATION_PAUSE_TIME);
    }
    xOutputCache.value = burnStore.output?.x;
    yOutputCache.value = burnStore.output?.y;
  },
);
</script>

<style scoped lang="scss">
.output {
  ::v-deep(.output__label) {
    display: none;
  }
}

.button {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 0;
  height: 44px;

  span {
    position: absolute;
    margin: auto;
    text-align: center;
    z-index: 2;
    opacity: 1;
  }

  .progress {
    position: absolute;
    left: 0;
    top: 0;
    right: 0;
    bottom: 0;
    width: 0%;
    background: #946bec;
    opacity: 0;
    transition: all 0.3s ease;
  }

  &.rate-changed {
    cursor: none;
    pointer-events: none;
  }

  &.rate-changed .progress {
    opacity: 1;
    animation: progress 2s linear 0s;
  }

  @keyframes progress {
    0% {
      width: 0%;
    }
    5% {
      width: 0%;
    }
    10% {
      width: 15%;
    }
    30% {
      width: 40%;
    }
    50% {
      width: 55%;
    }
    80% {
      width: 100%;
    }
    98% {
      width: 100%;
      opacity: 1;
    }
    100% {
      width: 100%;
      opacity: 0;
    }
  }
}
</style>
